// src/components/Dashboard_ADM/Atendimento/FunilTab.tsx
// Kanban do funil de conversão — seção 8.2. GET /whatsapp-agent/funil

import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, GitBranch } from 'lucide-react';
import apiClient from '../../../services/api';
import { FunnelStage, FUNNEL_STAGES, LeadRow, AGENT_LABELS } from './shared';

const STAGE_COLORS: Record<FunnelStage, string> = {
  Contato: 'bg-gray-50 border-gray-300',
  'Em Análise': 'bg-amber-50 border-amber-300',
  Aprovado: 'bg-blue-50 border-blue-300',
  'Pagamento Enviado': 'bg-indigo-50 border-indigo-300',
  Pago: 'bg-emerald-50 border-emerald-300',
  Ativo: 'bg-green-50 border-green-300',
};

const FunilTab: React.FC = () => {
  const [board, setBoard] = useState<Record<FunnelStage, LeadRow[]> | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<Record<FunnelStage, LeadRow[]>>('/whatsapp-agent/funil');
      setBoard(data);
    } catch {
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <GitBranch className="w-6 h-6 text-blue-600" /> Funil de Conversão
            </h2>
            <p className="text-gray-600">Cada lead por etapa — Contato até Ativo</p>
          </div>
          <button onClick={fetchBoard}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>
      ) : !board ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
          Não foi possível carregar o funil
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {FUNNEL_STAGES.map((stage) => (
            <div key={stage} className={`rounded-2xl border-2 ${STAGE_COLORS[stage]} p-4 min-h-[220px]`}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-black text-gray-800 text-sm">{stage}</p>
                <span className="px-2 py-0.5 rounded-full bg-white text-xs font-black text-gray-700">
                  {board[stage]?.length ?? 0}
                </span>
              </div>
              <div className="space-y-2">
                {(board[stage] ?? []).map((lead) => (
                  <div key={lead.affiliateId} className="bg-white rounded-xl border border-gray-200 p-3">
                    <p className="font-bold text-gray-900 text-sm truncate">{lead.schoolName}</p>
                    <p className="text-xs text-gray-500 truncate">{lead.responsibleName || lead.whatsapp || '—'}</p>
                    {lead.currentAgent && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700">
                        {AGENT_LABELS[lead.currentAgent]}
                      </span>
                    )}
                  </div>
                ))}
                {(board[stage] ?? []).length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-6">Vazio</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FunilTab;
